import { useState, useEffect, useRef } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { defaultLanguageWithLabels } from 'utils/languages';
import {
  Button,
  Grid,
  InputLabel,
  TextField,
  Box,
  Typography,
  SvgIcon,
} from '@mui/material';
import { useGlobalStore } from 'store/globalStore';
import { AsyncPaginate } from 'react-select-async-paginate';
import { Controller } from 'react-hook-form';
import { Form } from 'react-bootstrap';
import Modal from '../../components/common/Modal';
import Uploader from '../../components/uploader/Uploader';
import CancelIcon from '@mui/icons-material/Cancel';
import LinearProgress from '@mui/material/LinearProgress';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import NotInterestedOutlinedIcon from '@mui/icons-material/NotInterestedOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Tooltip } from 'components/Tooltip';
import { useAppProviderStore } from 'store/appProviderCopyStore';
import { CRUD_STATES } from 'constants/app';
import ClosedCaptionIcon from '@mui/icons-material/ClosedCaption';
import Chip from '@mui/material/Chip';
import SurroundSoundIcon from '@mui/icons-material/SurroundSound';
import { useNotification } from 'hooks/useNotification';
import { useUploadData } from 'hooks/useUploadData';
import { adminAPI, mediaAPI, videoAPI } from 'services/admin';

function StepOne(props) {
  const {
    register,
    reset,
    control,
    getValues,
    setValue,
    videoProps,
    activeStep,
    languages,
    viewFields,
    videoFiles,
    setVideoFiles,
    setSubtitleFiles,
    legendaIdioma,
    setLegendaIdioma,
    setCanAddSubtitle,
    canAddSubtitle,
    setCanAddAudio,
    canAddAudio,
    setAudioFiles,
    audioFiles,
    setAudioIdioma,
    audioIdioma,
    idiomaVideo,
    subtitleFiles,
    setIsEdit,
    typeItem,
  } = props;
  const {
    // videoMainUploadProgress,
    audioMainUploadProgress,
    subtitleMainUploadProgress,
    isLoading,
    setIsLoading,
    API_KEY,
  } = useAppProviderStore();
  const { setNotify } = useNotification();
  const location = useLocation();
  const [auth, setAuth] = useOutletContext();
  const [confirmDialogAction, setConfirmDialogAction] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
  });
  const [legend, setLegend] = useState(null);
  const [audio, setAudio] = useState(null);
  const [showUploadButton, setShowUploadButton] = useState(false);
  const [isDisableButton, setIsDisableButton] = useState(true);
  const [reprocessMedia, setReprocessMedia] = useState(false);

  const fileInputVideoRef = useRef(null);
  const fileInputLegendaRef = useRef(null);
  const fileInputAudioRef = useRef(null);
  const { showConfirmationButton } = useState(true);
  const session = auth;
  const idToken = session.getIdToken().getJwtToken();
  const userId = session.getIdToken().payload.sub;
  const userName = session.getIdToken().payload.username;
  const { setCancelUpload, setIsExistVideo } = useGlobalStore();
  const { getFormatedFileUploadById } = useUploadData();
  const videoMainUploadProgress = getFormatedFileUploadById('video_file');

  // * Principais formatos:
  //     * AIV,MP4,MOV,WEBM,MXF,WMV,MPEG,3GP,ASF,M4V
  //     * Formatos menos usados que podem ser que necessitem aceitar para transcodificar

  // 1 - Post para domain/admin/contents só com header Authorization vai retornar id da mídia criada
  // 2 - Post para domain/admin/media passando mediaContentId {id recebido no passo 1} retorna messa url assinada para upload da mídia
  // 3 - Put para a url assinada enviando o arquivo header content-type
  useEffect(() => {
    if (isLoading === false) {
      handleResetLanguage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let videoLanguage = getValues('video_language');
    if (videoLanguage?.value) {
      setShowUploadButton(true);
    } else {
      setShowUploadButton(false);
    }

    if (videoFiles?.mediaId?.status && videoFiles?.mediaId?.status === "ERROR") {
      setReprocessMedia(true);
    }
  });

  useEffect(() => {
    if (
      location?.state?.action === CRUD_STATES.EDIT ||
      location?.state?.action === CRUD_STATES.VIEW
    ) {
      setIsEdit(true);
    } else {
      setIsEdit(false);
    }

    if (audioIdioma.length === audioFiles.length) {
      setCanAddAudio(false);
    }
    if (legendaIdioma.length === subtitleFiles.length) {
      setCanAddSubtitle(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioIdioma, audioFiles]);

  useEffect(() => {
    setIsExistVideo(videoFiles?.mediaId?.name || videoFiles?.mediaId?.fileName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFiles?.mediaId?.name, videoFiles?.mediaId?.fileName]);

  const submitReprocessMedia = (mediaId) => {
    return new Promise((resolve, reject) => {
      setIsLoading(true);
      const path = `/admin/contents/${mediaId}/process`;

      const data = {
        method: 'POST',
        body: {
          mediaId: mediaId,
        },
        headers: {
          'x-api-key': API_KEY
        }
      };

      return adminAPI
        .post(path, data)
        .then(resp => {
          setIsLoading(false);
          setReprocessMedia(false);
          setNotify({
            isOpen: true,
            message: `Transcodificação da mídia ${mediaId} iniciada com sucesso!`,
            type: 'success',
            title: 'Sucesso!',
          });
          resolve(resp);
        })
        .catch(error => {
          setIsLoading(false);
          setReprocessMedia(false);
          setNotify({
            isOpen: true,
            message: `Erro para reprocessar a mídia ${mediaId}`,
            type: 'error',
            title: 'Erro!',
          });
          console.log(error);
          reject(error);
        });
    });
  }

  const submitMediaDeleteCancel = (mediaId, id, version) => {
    return new Promise((resolve, reject) => {
      setIsLoading(true);
      const path = `/${id}/versions/${version}/media/${mediaId}`;
      const session = auth;

      const data = {
        createdBy: {
          userId: session.getIdToken().payload.sub,
          name: session.getIdToken().payload.username,
        },
        body: {
          fileIds: [mediaId],
        },
      };

      return videoAPI
        .del(path, data)
        .then(resp => {
          setIsLoading(false);
          setVideoFiles([]);
          setSubtitleFiles([]);
          setLegendaIdioma([]);
          setCanAddSubtitle(false);
          setCanAddAudio(false);
          setAudioFiles([]);
          setAudioIdioma([]);
          setValue('audio_idioma', null);
          setValue('subtitle', null);
          setValue('mediaId');
          setNotify({
            isOpen: true,
            message: resp.message,
            type: 'success',
            title: 'Sucesso!',
          });
          resolve(resp);
        })
        .catch(error => {
          setIsLoading(false);
          setIsLoading(false);
          setNotify({
            isOpen: true,
            message: error.message,
            type: 'error',
            title: 'Passo 1: Erro ao excluir mídia.',
          });
          console.log(error);
        });
    });
  };

  async function handleDeleteMedia(mediaId, videoUploadProgress, id, version) {
    setConfirmDialogAction({ isOpen: false });
    if (uploadVerification(videoUploadProgress)) {
      setCancelUpload(true);
      console.log('Mídia removida pelo  usuário Durante o upload');
      setNotify({
        isOpen: true,
        message: 'Processo de upload interrompido pelo usuário!',
        type: 'success',
        title: 'Sucesso!',
      });
      submitMediaDeleteCancel(mediaId, id, version);
    } else {
      submitMediaDeleteCancel(mediaId, id, version);
    }
    setIsDisableButton(true);
  }

  async function handleReprocessMedia(mediaId) {
    setConfirmDialogAction({ isOpen: false });
    submitReprocessMedia(mediaId);
    setIsDisableButton(true);
  }

  const handleResetLanguage = () => {
    setTimeout(() => {
      setValue('subtitle', null);
      setValue('audio_idioma', null);
    }, 500);
  };

  async function handleDeleteSubtitle(file, idioma, index) {
    setConfirmDialogAction({ isOpen: false });
    return new Promise((resolve, reject) => {
      setIsLoading(true);
      let mediaId = file.mediaId;
      if (!mediaId) {
        mediaId = getValues('mediaId');
      }
      const path = `/${mediaId}/subtitles/${file.subtitleId}`;

      return mediaAPI
        .del(path)
        .then(resp => {
          setIsLoading(false);

          deleteFilesFromList(subtitleFiles, setSubtitleFiles, index);
          deleteFilesFromList(legendaIdioma, setLegendaIdioma, index);
          setValue(
            'subtitle',
            deleteFilesFromList(legendaIdioma, false, index)
          );
          if (legendaIdioma.length === subtitleFiles.length) {
            setCanAddSubtitle(true);
          }
          setConfirmDialogAction({
            isOpen: false,
          });
          setNotify({
            isOpen: true,
            message: `Legenda removida com sucesso.`,
            type: 'success',
            title: 'Sucesso!',
          });
          resolve(resp);
        })
        .catch(error => {
          setIsLoading(false);
          setConfirmDialogAction({
            isOpen: false,
          });
          setNotify({
            isOpen: true,
            message: error.details ? error.details : error.message,
            type: 'error',
            title: 'Passo 1: Erro ao excluir legenda.',
          });
          console.log(error);
        });
    });
  }

  async function handleDeleteAudioIdioma(file, idioma, index) {
    setConfirmDialogAction({ isOpen: false });
    return new Promise((resolve, reject) => {
      setIsLoading(true);
      let mediaId = getValues('mediaId');
      const path = `/${mediaId}/audio-tracks/${file.audioTrackId}`;

      const data = {
        body: {
          fileIds: [file.audioTrackId],
        },
      };

      return mediaAPI
        .del(path, data)
        .then(resp => {
          setIsLoading(false);
          setConfirmDialogAction({
            isOpen: false,
          });
          deleteFilesFromList(audioFiles, setAudioFiles, index);
          deleteFilesFromList(audioIdioma, setAudioIdioma, index);
          setValue(
            'audio_idioma',
            deleteFilesFromList(audioIdioma, false, index)
          );
          setNotify({
            isOpen: true,
            message: 'Áudio removido com sucesso!',
            type: 'success',
            title: 'Sucesso!',
          });

          resolve(resp);
        })
        .catch(error => {
          setConfirmDialogAction({
            isOpen: false,
          });
          setIsLoading(false);
          setNotify({
            isOpen: true,
            message: error.message,
            type: 'error',
            title: 'Passo 1: Erro ao remover áudio.',
          });
          console.log(error);
        });
    });
  }

  const uploadVerification = file => {
    return !!(file?.percent > 0 && file?.percent < 100);
  };

  const ClickHandler = event => {
    event.preventDefault();
  };

  const deleteFilesFromList = (files, setFiles, indexToRemove) => {
    const updatedItems = files.filter((item, index) => index !== indexToRemove);
    setFiles && setFiles(updatedItems);
    return updatedItems;
  };

  return (
    <div style={{ position: 'relative' }} id="step-one">
      <div className="step-container" disabled={isLoading}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className="video_label-title">Vídeo</div>
          </Grid>
          <Grid item xs={4}>
            <Form.Group style={{ height: 'auto' }} controlId="formUnit">
              <InputLabel className="select-label">
                Idioma do áudio do vídeo
              </InputLabel>
              {videoFiles?.mediaId && (
                <TextField
                  value={videoFiles?.mediaId?.label || idiomaVideo?.label}
                  fullWidth
                  disabled={true}
                  variant="standard"
                  style={{ marginTop: 16 }}
                  id="video-language-audio-video"
                />
              )}
              {!videoFiles?.mediaId && (
                <Controller
                  name={'video_language'}
                  control={control}
                  render={({
                    field: { onChange, value, onBlur },
                    fieldState: { errors, isValid, isTouched, isDirty },
                    formState,
                  }) => (
                    <AsyncPaginate
                      styles={{
                        control: (baseStyles, state) => ({
                          ...baseStyles,
                          '&:hover': {
                            borderColor: 'transparent',
                            borderRadius: '0px',
                            border: 0,
                            borderBottom: '1px solid var(--gray-300, #D0D5DD)',
                          },
                          borderRadius: '0px',
                          border: 0,
                          borderBottom: '1px solid var(--gray-300, #D0D5DD)',
                          width: 'auto',
                          padding: 0,
                          margin: 0,
                          marginTop: '10px',
                          marginRight: '30px'

                        }),
                        singleValue: base => ({
                          ...base,
                          // padding: 5,
                          borderRadius: 6,
                          color: 'rgba(25, 28, 31, 1)',
                          fontSize: '15px',
                          display: 'flex',
                        }),
                        menuList: base => ({
                          ...base,
                          maxHeight: '150px',
                        }),
                      }}
                      theme={theme => ({
                        ...theme,
                        borderRadius: 0,
                        colors: {
                          ...theme.colors,
                          primary25: '#e9e9ea',
                          primary: '#0A6CCF',
                          width: 'auto',
                        },
                      })}
                      loadOptions={defaultLanguageWithLabels}
                      menuPosition="fixed"
                      noOptionsMessage={() => 'Nada encontrado'}
                      loadingMessage={() => 'Carregando...'}
                      isDisabled={videoFiles?.mediaId || viewFields}
                      isSearchable={true}
                      id="video-select-main-media-language"
                      placeholder="Selecione o idioma"
                      onChange={selectedOption => {
                        setIsDisableButton(false);
                        onChange(selectedOption);
                      }}
                    />
                  )}
                />
              )}
            </Form.Group>
          </Grid>
          {videoFiles?.mediaId && (
            <Grid item xs={8}>
              <Form.Group
                style={{ height: 'auto', paddingRight: 15 }}
                controlId="formVideoFile">
                <div className="file-container">
                  <div style={{ maxWidth: 300, marginRight: 15 }}>
                    <InputLabel
                      className="select-label"
                      style={{ marginBottom: 25, marginTop: 0 }}>
                      Arquivo
                    </InputLabel>

                    <Controller
                      name={'video_media'}
                      control={control}
                      render={({
                        field: { onChange, value, onBlur },
                        fieldState: { error },
                      }) => (
                        <span
                          style={{
                            fontWeight: '400',
                            color: '#0000CC',
                            borderBottom: '1px solid rgba(208, 213, 221, 1)',
                            textDecoration: 'none',
                            overflowWrap: 'break-word',
                          }}
                          onChange={e => {
                            onChange(e);
                            setIsDisableButton(true);
                          }}
                          value={
                            value || { value: '', label: 'Selecione o Idioma' }
                          }
                          onBlur={onBlur}
                          disabled={viewFields}
                          id="video-filename">
                          {videoFiles?.mediaId?.name ||
                            videoFiles?.mediaId?.fileName}
                        </span>
                      )}
                    />
                  </div>
            <Button
              color="info"
              id="video-reprocess-midia-file"
              style={{
                textTransform: 'capitalize',
                color: reprocessMedia ? 'black' : 'lightgray',
                backgroundColor: reprocessMedia ? 'initial' : 'transparent',
              }}
              startIcon={
                <SvgIcon style={{ color: reprocessMedia ? 'black' : 'lightgray' }}> {/**/}
                  <svg height="24" viewBox="0 0 24 24" width="24">
                    <path d="M0 0h24v24H0z" fill="none" />
                    <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" />
                  </svg>
                </SvgIcon>
              }
              onClick={async () => {
                setConfirmDialogAction({
                  isOpen: true,
                  title: (
                    <div className="modalTitleText">
                      Deseja reprocessar a mídia?
                    </div>
                  ),
                  subTitle: (
                    <div className="modalBodyText">
                      Você está retranscodificando o arquivo novamente,
                      deseja prosseguir?
                    </div>
                  ),
                  children: (
                    <div className="modalRemoveItem">
                      <SvgIcon>
                        <svg height="24" viewBox="0 0 24 24" width="24">
                          <path d="M0 0h24v24H0z" fill="none" />
                          <path d="M7 7h10v3l4-4-4-4v3H5v6h2V7zm10 10H7v-3l-4 4 4 4v-3h12v-6h-2v4z" />
                        </svg>
                      </SvgIcon>
                    </div>
                  ),
                  confirmTextButton: 'Sim',
                  color: 'error',
                  onConfirm: async () => {
                    await handleReprocessMedia(
                      getValues('mediaId')
                    );
                  },
                  showConfirmationButton: { showConfirmationButton },
                  closeTextButton: 'Não',
                });
              }}
              disabled={!reprocessMedia}
            >
              Reprocessar
            </Button>

                  <Button
                    color="error"
                    id="video-remove-midia-file"
                    style={{
                      textTransform: 'capitalize',
                      color: 'rgba(255, 98, 98, 1)',
                    }}
                    startIcon={
                      uploadVerification(videoMainUploadProgress) ? (
                        <CancelIcon />
                      ) : (
                        <NotInterestedOutlinedIcon />
                      )
                    }
                    onClick={async () => {
                      setConfirmDialogAction({
                        isOpen: true,
                        title: (
                          <div className="modalTitleText">
                            Deseja remover a mídia?
                          </div>
                        ),
                        subTitle: (
                          <div className="modalBodyText">
                            Você está deletando o arquivo permanentemente,
                            deseja prosseguir?
                          </div>
                        ),
                        children: (
                          <div className="modalRemoveItem">
                            <DeleteOutlineOutlinedIcon
                              color="error"
                              fontSize="12px"
                            />
                          </div>
                        ),
                        confirmTextButton: 'Sim',
                        color: 'error',
                        onConfirm: async () => {
                          await handleDeleteMedia(
                            getValues('mediaId'),
                            videoMainUploadProgress,
                            getValues('id'),
                            getValues('version')
                          );
                        },
                        showConfirmationButton: { showConfirmationButton },
                        closeTextButton: 'Não',
                      });
                    }}
                    disabled={viewFields}>
                    {uploadVerification(videoMainUploadProgress)
                      ? 'Cancelar'
                      : 'Remover'}
                  </Button>
                </div>
              </Form.Group>
            </Grid>
          )}

          <Grid item xs={8}>
            <div className="file-container">
              <div>
                <InputLabel
                  className={
                    videoFiles?.mediaId
                      ? 'select-label  hidden'
                      : 'select-label'
                  }
                  style={{ marginBottom: 12, marginTop: 0 }}>
                  Arquivo
                </InputLabel>
                <Tooltip.Info
                  title="Atenção"
                  description="Antes de selecionar o arquivo selecione o idioma"
                  show={
                    !videoFiles?.mediaId &&
                    (videoFiles?.mediaId ||
                      !showUploadButton ||
                      viewFields ||
                      isDisableButton)
                  }>
                  <Uploader
                    register={register}
                    name={'video_file'}
                    icon={<FileUploadOutlinedIcon />}
                    accept={
                      'video/AVI,video/MP4,video/MOV,video/WEBM,video/MXF,video/WMV, video/MPEG, video/3GP, video/ASF, video/M4V,video/*'
                    }
                    fileInputRef={fileInputVideoRef}
                    file={videoFiles}
                    title={'Selecionar vídeo'}
                    isDisabled={
                      videoFiles?.mediaId ||
                      !showUploadButton ||
                      viewFields ||
                      isDisableButton
                    }
                    apiName={'admin'}
                    path={`/admin/media`}
                    token={idToken}
                    id={getValues('id')}
                    language={getValues('video_language')}
                    userId={userId}
                    userName={userName}
                    setIsLoading={setIsLoading}
                    isLoading={isLoading}
                    setProps={setVideoFiles}
                    setNotify={setNotify}
                    type={{ label: 'video', name: 'MAIN' }}
                    getValues={getValues}
                    setValue={setValue}
                    activeStep={activeStep}
                    setAuth={setAuth}
                    typeItem={typeItem}
                  />
                </Tooltip.Info>
                {videoMainUploadProgress?.percent > 0 &&
                  videoMainUploadProgress?.percent < 100 && (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box sx={{ width: '100%', mr: 1 }}>
                        <label style={{ fontSize: '12px', color: 'blue' }}>
                          {videoMainUploadProgress?.name}
                        </label>
                        <LinearProgress
                          variant="determinate"
                          value={videoMainUploadProgress?.percent}
                        />
                      </Box>
                      <Box sx={{ minWidth: 35 }}>
                        <Typography variant="body2" color="text.secondary">
                          {videoMainUploadProgress?.percent}%
                        </Typography>
                      </Box>
                    </Box>
                  )}
              </div>
            </div>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div className="video_label-title" style={{ marginTop: 40 }}>
              Legendas
            </div>
          </Grid>
          {subtitleFiles.map((file, index) => (
            <Grid container spacing={2} key={index} sx={{ padding: '15px' }}>
              <Grid item xs={4}>
                <Form.Group
                  style={{ height: 'auto' }}
                  controlId="formLegendaIdioma">
                  <InputLabel
                    className="form-label"
                    id="select-standard-label"
                    htmlFor="legendaIdioma">
                    Idioma
                  </InputLabel>
                  <TextField
                    value={file?.label}
                    fullWidth
                    disabled={!!subtitleFiles[index] || !!viewFields}
                    variant="standard"
                    style={{ marginTop: 6 }}
                    id="video-subtitles-language"
                  />
                </Form.Group>
              </Grid>

              <Grid item xs={8}>
                <div
                  className="file-container"
                  style={{ flexDirection: 'row', display: 'flex' }}>
                  <div style={{ maxWidth: 300, marginRight: 15 }}>
                    <InputLabel
                      className="select-label"
                      style={{ marginBottom: 24, marginTop: 0 }}>
                      Arquivo
                      <span
                        name={'legenda_' + index + '_idioma'}
                        href="/"
                        onClick={ClickHandler}
                        disabled={viewFields}
                        id="video-subtitle-accessibility">
                        {file.accessibility === 'true' ||
                        file.accessibility === true ? (
                          <Chip
                            icon={<ClosedCaptionIcon color="primary" />}
                            label="Legenda - Closed Caption"
                            sx={{ fontSize: '12px', marginLeft: '5px' }}
                            size="small"
                          />
                        ) : (
                          ''
                        )}
                      </span>
                    </InputLabel>
                    <span
                      name={'legenda_' + index + '_idioma'}
                      // href="/"
                      // onClick={ClickHandler}
                      style={{
                        fontWeight: '400',
                        color: '#0000CC',
                        borderBottom: '1px solid rgba(208, 213, 221, 1)',
                        textDecoration: 'none',
                        overflowWrap: 'break-word',
                      }}
                      disabled={viewFields}
                      id="video-subtitle-filename">
                      {file.name}
                    </span>
                  </div>
                  <Button
                    disabled={
                      subtitleMainUploadProgress?.percent < 100 ||
                      subtitleMainUploadProgress === false ||
                      viewFields
                        ? true
                        : false
                    }
                    color="error"
                    id="video-remove-subtitle"
                    sx={{
                      textTransform: 'capitalize',
                      color:
                        subtitleMainUploadProgress?.percent < 100 ||
                        subtitleMainUploadProgress === false ||
                        viewFields
                          ? 'rgba(0, 0, 0, 0.38)'
                          : 'rgba(255, 98, 98, 1)',
                      marginBottom: '8px',
                    }}
                    startIcon={<NotInterestedOutlinedIcon />}
                    onClick={async () => {
                      setConfirmDialogAction({
                        isOpen: true,
                        title: (
                          <div className="modalTitleText">
                            Deseja remover a legenda?
                          </div>
                        ),
                        subTitle: (
                          <div className="modalBodyText">
                            Você está deletando o arquivo permanentemente,
                            deseja prosseguir?
                          </div>
                        ),
                        children: (
                          <div className="modalRemoveItem">
                            <DeleteOutlineOutlinedIcon
                              color="error"
                              fontSize="12px"
                            />
                          </div>
                        ),
                        confirmTextButton: 'Sim',
                        color: 'error',
                        onConfirm: async () => {
                          await handleDeleteSubtitle(
                            file,
                            legendaIdioma[index],
                            index
                          );
                        },
                        showConfirmationButton: { showConfirmationButton },
                        closeTextButton: 'Não',
                      });
                    }}>
                    Remover
                  </Button>
                </div>
              </Grid>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Form.Group
              className="mb-3"
              style={{ height: 'auto', marginTop: 10 }}
              controlId="formUnit">
              <Controller
                name={'subtitle'}
                control={control}
                render={({ field: { onChange, value } }) => {
                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '224px auto',
                        gap: 2,
                      }}>
                      <Box>
                        <InputLabel
                          className="form-label"
                          id="select-standard-label"
                          htmlFor="subtitle">
                          Idioma
                        </InputLabel>
                        <AsyncPaginate
                          styles={{
                            control: baseStyles => ({
                              ...baseStyles,
                              '&:hover': {
                                borderColor: 'transparent',
                                borderRadius: '0px',
                                border: 0,
                                borderBottom:
                                  '1px solid var(--gray-300, #D0D5DD)',
                              },
                              borderRadius: '0px',
                              border: 0,
                              borderBottom:
                                '1px solid var(--gray-300, #D0D5DD)',
                              width: 'auto',
                              padding: 0,
                              margin: 0,
                            }),
                            singleValue: base => ({
                              ...base,
                              borderRadius: 6,
                              color: 'rgba(25, 28, 31, 1)',
                              fontSize: '15px',
                              display: 'flex',
                            }),
                            menuList: base => ({
                              ...base,
                              maxHeight: '150px',
                            }),
                          }}
                          theme={theme => ({
                            ...theme,
                            borderRadius: 0,
                            colors: {
                              ...theme.colors,
                              primary25: '#e9e9ea',
                              primary: '#0A6CCF',
                              width: 'auto',
                            },
                          })}
                          loadOptions={defaultLanguageWithLabels}
                          value={
                            value || { value: '', label: 'Selecione o Idioma' }
                          }
                          menuPosition="fixed"
                          noOptionsMessage={() => 'Nada encontrado'}
                          loadingMessage={() => 'Carregando...'}
                          isDisabled={
                            (!getValues('mediaId') && !canAddSubtitle) ||
                            viewFields
                          }
                          isSearchable={true}
                          id="video-subtitle-main-media"
                          placeholder="Selecione o idioma"
                          onChange={selectedOption => {
                            onChange(selectedOption);
                            setCanAddSubtitle(true);
                            setLegend(selectedOption);
                          }}
                        />
                      </Box>
                      <Box>
                        <InputLabel
                          className="select-label"
                          style={{ marginTop: 2, marginBottom: 10 }}>
                          Arquivo
                        </InputLabel>
                        {videoProps?.id && (
                          <Tooltip.Info
                            title="Atenção"
                            description="Antes de selecionar o arquivo selecione o idioma"
                            show={!canAddSubtitle || viewFields || !value}>
                            <Uploader
                              reset={reset}
                              register={register}
                              name={'legenda_media'}
                              icon={<FileUploadOutlinedIcon />}
                              accept={'.srt,.txt,.vvt'}
                              fileInputRef={fileInputLegendaRef}
                              file={subtitleFiles}
                              title="Selecionar legenda"
                              isDisabled={
                                !canAddSubtitle || viewFields || !value
                              }
                              apiName={'admin'}
                              path={`/admin/media/${getValues(
                                'mediaId'
                              )}/subtitles`}
                              token={idToken}
                              id={videoProps.id}
                              language={legend}
                              languages={languages}
                              userId={userId}
                              userName={userName}
                              setIsLoading={setIsLoading}
                              isLoading={isLoading}
                              setProps={setSubtitleFiles}
                              setNotify={setNotify}
                              type={{ label: 'legenda', name: 'MAIN' }}
                              getValues={getValues}
                              setValue={setValue}
                              setAuth={setAuth}
                              isAcessibilityCheck={true}
                              nameIsAcessibilityCheck={'legend'}
                            />
                          </Tooltip.Info>
                        )}
                      </Box>
                      {subtitleMainUploadProgress?.percent > 0 &&
                        subtitleMainUploadProgress?.percent < 100 && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <label
                                style={{ fontSize: '12px', color: 'blue' }}>
                                {subtitleMainUploadProgress?.name}
                              </label>
                              <LinearProgress
                                variant="determinate"
                                value={subtitleMainUploadProgress?.percent}
                                sx={{ width: '100%' }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary">
                                {subtitleMainUploadProgress?.percent}%
                              </Typography>
                            </Box>
                          </Box>
                        )}
                    </Box>
                  );
                }}
              />
            </Form.Group>
          </Grid>
        </Grid>
        {/* )} */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <div
              className="video_label-title"
              style={{ marginTop: 35, marginBottom: 25 }}>
              Faixas de áudio
            </div>
          </Grid>

          {audioFiles &&
            audioFiles.map((file, index) => (
              <Grid container spacing={2} key={index} sx={{ padding: '15px' }}>
                <Grid item xs={4}>
                  <Form.Group
                    className="mb-3"
                    style={{ height: 'auto' }}
                    controlId="formUnit">
                    <InputLabel
                      className="form-label"
                      id="select-standard-label"
                      htmlFor="audioIdioma">
                      Idioma
                    </InputLabel>
                    <TextField
                      value={file?.label}
                      fullWidth
                      disabled={file?.label || viewFields}
                      variant="standard"
                      style={{ marginTop: 6 }}
                      id="video-audio-track-language"
                    />
                  </Form.Group>
                </Grid>

                <Grid item xs={8}>
                  <div
                    className="file-container"
                    style={{ flexDirection: 'row', display: 'flex' }}>
                    <div style={{ maxWidth: 300, marginRight: 30 }}>
                      <InputLabel
                        className="select-label"
                        style={{ marginBottom: 24, marginTop: 0 }}>
                        Arquivo
                        <span
                          name={'audio_' + index + '_idioma'}
                          href="/"
                          onClick={ClickHandler}
                          disabled={viewFields}
                          id="video-audio-accessibility">
                          {file.accessibility === 'true' ||
                          file.accessibility === true ? (
                            <Chip
                              icon={<SurroundSoundIcon color="primary" />}
                              label="Áudio descrição"
                              sx={{ fontSize: '12px', marginLeft: '5px' }}
                              size="small"
                            />
                          ) : (
                            ''
                          )}
                        </span>
                      </InputLabel>
                      <span
                        name={'audio_' + index + '_idioma'}
                        href="/"
                        onClick={ClickHandler}
                        style={{
                          fontWeight: '400',
                          color: '#0000CC',
                          borderBottom: '1px solid rgba(208, 213, 221, 1)',
                          overflowWrap: 'break-word',
                        }}
                        disabled={viewFields}
                        id="video-audiotrack-filename">
                        {file.name}
                      </span>
                    </div>
                    <Button
                      disabled={
                        audioMainUploadProgress?.percent < 100 ||
                        audioMainUploadProgress === false ||
                        viewFields
                          ? true
                          : false
                      }
                      color="error"
                      id="video-remove-audiotrack"
                      sx={{
                        textTransform: 'capitalize',
                        color:
                          audioMainUploadProgress?.percent < 100 ||
                          audioMainUploadProgress === false ||
                          viewFields
                            ? 'rgba(0, 0, 0, 0.38)'
                            : 'rgba(255, 98, 98, 1)',
                        marginBottom: '8px',
                      }}
                      startIcon={<NotInterestedOutlinedIcon />}
                      onClick={async () => {
                        setConfirmDialogAction({
                          isOpen: true,
                          title: (
                            <div
                              className="modalTitleText"
                              id="video-remove-midias-confirmação">
                              Deseja remover o áudio?
                            </div>
                          ),
                          subTitle: (
                            <div className="modalBodyText">
                              Você está deletando o arquivo permanentemente,
                              deseja prosseguir?
                            </div>
                          ),
                          children: (
                            <div className="modalRemoveItem">
                              <DeleteOutlineOutlinedIcon
                                color="error"
                                fontSize="12px"
                              />
                            </div>
                          ),
                          confirmTextButton: 'Sim',
                          color: 'error',
                          onConfirm: async () => {
                            await handleDeleteAudioIdioma(
                              file,
                              legendaIdioma[index],
                              index
                            );
                          },
                          showConfirmationButton: { showConfirmationButton },
                          closeTextButton: 'Não',
                        });
                      }}>
                      Remover
                    </Button>
                  </div>
                </Grid>
              </Grid>
            ))}
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Form.Group
              className="mb-3"
              style={{ height: 'auto', marginTop: 10 }}
              controlId="formUnit"
              id="video-audio-track">
              <Controller
                name={'audio_idioma'}
                control={control}
                render={({
                  field: { onChange, value, onBlur },
                  fieldState: { errors, isValid, isTouched, isDirty },
                  formState,
                }) => {
                  return (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '224px auto',
                        gap: 2,
                      }}>
                      <Box>
                        <InputLabel
                          className="form-label"
                          id="select-standard-label"
                          htmlFor="units">
                          Idioma
                        </InputLabel>
                        <AsyncPaginate
                          styles={{
                            control: (baseStyles, state) => ({
                              ...baseStyles,
                              '&:hover': {
                                borderColor: 'transparent',
                                borderRadius: '0px',
                                border: 0,
                                borderBottom:
                                  '1px solid var(--gray-300, #D0D5DD)',
                              },
                              borderRadius: '0px',
                              border: 0,
                              borderBottom:
                                '1px solid var(--gray-300, #D0D5DD)',
                              width: 'auto',
                              padding: 0,
                              margin: 0,
                            }),
                            singleValue: base => ({
                              ...base,
                              borderRadius: 6,
                              color: 'rgba(25, 28, 31, 1)',
                              fontSize: '15px',
                              display: 'flex',
                            }),
                            menuList: base => ({
                              ...base,
                              maxHeight: '150px',
                            }),
                          }}
                          theme={theme => ({
                            ...theme,
                            borderRadius: 0,
                            colors: {
                              ...theme.colors,
                              primary25: '#e9e9ea',
                              primary: '#0A6CCF',
                              width: 'auto',
                            },
                          })}
                          menuPosition="fixed"
                          noOptionsMessage={() => 'Nada encontrado'}
                          loadingMessage={() => 'Carregando...'}
                          loadOptions={defaultLanguageWithLabels}
                          value={
                            value || { value: '', label: 'Selecione o Idioma' }
                          }
                          isDisabled={
                            (!getValues('mediaId') && !canAddAudio) ||
                            viewFields
                          }
                          isSearchable={true}
                          id="video-audiotrack-main-media"
                          placeholder="Selecione o idioma"
                          onChange={selectedOption => {
                            onChange(selectedOption);
                            setCanAddAudio(true);
                            setAudio(selectedOption);
                          }}
                        />
                      </Box>
                      <Box>
                        <InputLabel
                          className="select-label"
                          style={{ marginTop: 0, marginBottom: 12 }}>
                          Arquivo
                        </InputLabel>
                        {videoProps?.id && (
                          <Tooltip.Info
                            title="Atenção"
                            description="Antes de selecionar o arquivo selecione o idioma"
                            show={!canAddAudio || viewFields || !value}>
                            <Uploader
                              reset={reset}
                              register={register}
                              name={'audio_file'}
                              icon={<FileUploadOutlinedIcon />}
                              accept=".mp3, .wav, .aiff, .flac"
                              fileInputRef={fileInputAudioRef}
                              file={audioFiles}
                              title={'Selecionar faixa'}
                              isDisabled={!canAddAudio || viewFields || !value}
                              apiName={'admin'}
                              path={`/admin/media/${videoFiles?.mediaId?.mediaId}/audio-tracks`}
                              token={idToken}
                              id={videoProps.id}
                              language={audio}
                              userId={userId}
                              userName={userName}
                              setIsLoading={setIsLoading}
                              isLoading={isLoading}
                              setProps={setAudioFiles}
                              setNotify={setNotify}
                              type={{
                                label: 'audio',
                                name: 'MAIN',
                                model: 'MAIN',
                              }}
                              getValues={getValues}
                              setValue={setValue}
                              setAuth={setAuth}
                              isAcessibilityCheck={true}
                              nameIsAcessibilityCheck={'audio'}
                            />
                          </Tooltip.Info>
                        )}
                      </Box>
                      {audioMainUploadProgress?.percent > 0 &&
                        audioMainUploadProgress?.percent < 100 && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <label
                                style={{ fontSize: '12px', color: 'blue' }}>
                                {audioMainUploadProgress?.name}
                              </label>
                              <LinearProgress
                                variant="determinate"
                                value={audioMainUploadProgress?.percent}
                                sx={{ width: '100%' }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 35 }}>
                              <Typography
                                variant="body2"
                                color="text.secondary">
                                {audioMainUploadProgress?.percent}%
                              </Typography>
                            </Box>
                          </Box>
                        )}
                    </Box>
                  );
                }}
              />
            </Form.Group>
          </Grid>
        </Grid>
      </div>
      <Modal
        confirmDialogAction={confirmDialogAction}
        setConfirmDialogAction={setConfirmDialogAction}
      />
    </div>
  );
}

export default StepOne;
